/**
 * Related Products Functionality
 * Add this to your theme's main JS file or create a new file
 */

class RelatedProducts {
  constructor() {
    this.init();
  }

  init() {
    this.bindEvents();
  }

  bindEvents() {
    // Add to cart functionality for related products
    document.addEventListener('click', (e) => {
      if (e.target.classList.contains('related-add-to-cart')) {
        e.preventDefault();
        this.addToCart(e.target);
      }
    });

    // Quick view functionality (optional)
    document.addEventListener('click', (e) => {
      if (e.target.closest('.related-product-overlay')) {
        e.preventDefault();
        const productCard = e.target.closest('.related-product-card');
        const productLink = productCard.querySelector('.related-product-link');
        if (productLink) {
          // You can implement quick view modal here or just navigate
          window.location.href = productLink.href;
        }
      }
    });
  }

  async addToCart(button) {
    const variantId = button.dataset.variantId;
    const productTitle = button.dataset.productTitle;
    
    if (!variantId) {
      console.error('No variant ID found');
      return;
    }

    // Set loading state
    this.setButtonState(button, 'loading');

    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          id: variantId,
          quantity: 1
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      // Set success state
      this.setButtonState(button, 'success');
      
      // Update cart drawer/count if available
      this.updateCartUI();
      
      // Show success message
      this.showSuccessMessage(productTitle);
      
      // Reset button after delay
      setTimeout(() => {
        this.setButtonState(button, 'default');
      }, 2000);

    } catch (error) {
      console.error('Error adding to cart:', error);
      
      // Set error state
      this.setButtonState(button, 'error');
      
      // Show error message
      this.showErrorMessage();
      
      // Reset button after delay
      setTimeout(() => {
        this.setButtonState(button, 'default');
      }, 3000);
    }
  }

  setButtonState(button, state) {
    // Remove all state classes
    button.classList.remove('loading', 'success', 'error');
    
    // Store original text if not already stored
    if (!button.dataset.originalText) {
      button.dataset.originalText = button.textContent;
    }

    switch (state) {
      case 'loading':
        button.classList.add('loading');
        button.disabled = true;
        button.textContent = 'Adding...';
        break;
        
      case 'success':
        button.classList.add('success');
        button.disabled = true;
        button.textContent = 'Added!';
        break;
        
      case 'error':
        button.classList.add('error');
        button.disabled = true;
        button.textContent = 'Error';
        break;
        
      case 'default':
      default:
        button.disabled = false;
        button.textContent = button.dataset.originalText || 'Add to Cart';
        break;
    }
  }

  async updateCartUI() {
    try {
      // Update cart count if cart count element exists
      const cartCountElements = document.querySelectorAll('.cart-count, [data-cart-count]');
      
      if (cartCountElements.length > 0) {
        const cartResponse = await fetch(`${window.Shopify.routes.root}cart.js`);
        const cartData = await cartResponse.json();
        
        cartCountElements.forEach(element => {
          element.textContent = cartData.item_count;
          
          // Add animation class if available
          element.classList.add('cart-updated');
          setTimeout(() => {
            element.classList.remove('cart-updated');
          }, 600);
        });
      }

      // Trigger cart drawer refresh if using Dawn's cart drawer
      if (window.theme && window.theme.cartDrawer) {
        window.theme.cartDrawer.refresh();
      }

      // Dispatch custom event for other cart integrations
      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: { source: 'related-products' }
      }));

    } catch (error) {
      console.error('Error updating cart UI:', error);
    }
  }

  showSuccessMessage(productTitle) {
    // Create success notification
    const notification = this.createNotification(
      `"${productTitle}" added to cart!`,
      'success'
    );
    
    this.showNotification(notification);
  }

  showErrorMessage() {
    // Create error notification
    const notification = this.createNotification(
      'Unable to add item to cart. Please try again.',
      'error'
    );
    
    this.showNotification(notification);
  }

  createNotification(message, type = 'success') {
    const notification = document.createElement('div');
    notification.className = `related-products-notification related-products-notification--${type}`;
    notification.setAttribute('role', 'alert');
    notification.setAttribute('aria-live', 'assertive');
    notification.innerHTML = `
      <div class="related-products-notification__content">
        <span class="related-products-notification__message">${message}</span>
        <button class="related-products-notification__close" aria-label="Close notification">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
        </button>
      </div>
    `;

    // Add close functionality
    notification.querySelector('.related-products-notification__close').addEventListener('click', () => {
      this.hideNotification(notification);
    });

    return notification;
  }

  showNotification(notification) {
    // Add notification styles if not already present
    this.addNotificationStyles();
    
    // Add to page
    document.body.appendChild(notification);
    
    // Trigger animation
    requestAnimationFrame(() => {
      notification.classList.add('related-products-notification--visible');
    });

    // Auto-hide after 5 seconds
    setTimeout(() => {
      this.hideNotification(notification);
    }, 5000);
  }

  hideNotification(notification) {
    notification.classList.remove('related-products-notification--visible');
    
    setTimeout(() => {
      if (notification.parentNode) {
        notification.parentNode.removeChild(notification);
      }
    }, 300);
  }

  addNotificationStyles() {
    // Check if styles already added
    if (document.querySelector('#related-products-notification-styles')) {
      return;
    }

    const styles = document.createElement('style');
    styles.id = 'related-products-notification-styles';
    styles.textContent = `
      .related-products-notification {
        position: fixed;
        top: 2rem;
        right: 2rem;
        z-index: 10000;
        max-width: 40rem;
        padding: 1.5rem;
        border-radius: 0.8rem;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
        transform: translateX(100%);
        transition: transform 0.3s ease, opacity 0.3s ease;
        opacity: 0;
      }
      
      .related-products-notification--visible {
        transform: translateX(0);
        opacity: 1;
      }
      
      .related-products-notification--success {
        background: #2e7d32;
        color: white;
      }

      .related-products-notification--error {
        background: #c62828;
        color: white;
      }
      
      .related-products-notification__content {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 1rem;
      }
      
      .related-products-notification__message {
        font-weight: 500;
        font-size: 1.4rem;
      }
      
      .related-products-notification__close {
        background: none;
        border: none;
        color: currentColor;
        cursor: pointer;
        padding: 0.4rem;
        border-radius: 0.4rem;
        transition: background-color 0.2s ease;
      }
      
      .related-products-notification__close:hover {
        background-color: rgba(255, 255, 255, 0.2);
      }
      
      @media screen and (max-width: 768px) {
        .related-products-notification {
          top: 1rem;
          right: 1rem;
          left: 1rem;
          max-width: none;
        }
      }
    `;
    
    document.head.appendChild(styles);
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new RelatedProducts();
  });
} else {
  new RelatedProducts();
}

// Optional: Advanced analytics tracking
function trackRelatedProductClick(productId, productTitle, position) {
  // Google Analytics 4
  if (typeof gtag !== 'undefined') {
    gtag('event', 'select_item', {
      item_list_id: 'related_products',
      item_list_name: 'Related Products',
      items: [{
        item_id: productId,
        item_name: productTitle,
        index: position
      }]
    });
  }
  
  // Facebook Pixel
  if (typeof fbq !== 'undefined') {
    fbq('track', 'ViewContent', {
      content_name: productTitle,
      content_ids: [productId],
      content_type: 'product'
    });
  }
}

// Optional: Add tracking to product clicks
document.addEventListener('click', (e) => {
  if (e.target.closest('.related-product-link')) {
    const productCard = e.target.closest('.related-product-card');
    const productId = productCard.dataset.productId;
    const productTitle = productCard.querySelector('.related-product-title a').textContent;
    const position = Array.from(productCard.parentNode.children).indexOf(productCard);
    
    trackRelatedProductClick(productId, productTitle, position);
  }
});